// ─────────────────────────────────────────────────────────────────────────────
//  services/legacyMigration.js — One-time migration: localStorage → relational DB
//
//  Pre-Phase-8 records exist only in the owner's localStorage (keyed by user ID).
//  Team members can't see them because the relational tables are empty.
//  This module reads localStorage, filters for unmigrated records (no _rid), and
//  calls the existing bridge/persist functions to write them to the relational DB.
//
//  Idempotency: each record uses a stable key `migrate-{collection}-{oldId}` so
//  the migration is safe to re-run — the DB replays previous results.
//
//  Called once automatically from AppContext after relational data loads.
// ─────────────────────────────────────────────────────────────────────────────
import { rpc } from './rpc';

const isUUID = (v) => typeof v === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(v);
const hasRid = (r) => r && (r._rid || isUUID(r.id));

// Stable idempotency key per record — safe to re-run
const migrateKey = (collection, oldId) => `migrate-${collection}-${String(oldId).slice(0, 30)}`;

// ── Master data migration ─────────────────────────────────────────────────

async function migrateProducts(records, businessId, token) {
  const results = []; let attempted = 0;
  for (const p of records) {
    if (hasRid(p) || p.deleted) continue;
    attempted++;
    try {
      const payload = {
        business_id: businessId,
        product: {
          product_name: p.productName || p.name || '',
          category: p.category || null,
          unit: p.unit || 'piece',
          cost_price: parseFloat(p.costPrice || 0),
          selling_price: parseFloat(p.sellingPrice || 0),
          opening_stock_qty: parseFloat(p.openingStockQty || 0),
          opening_stock_value: parseFloat(p.openingStockValue || 0),
        },
      };
      const r = await rpc('upsert_product', migrateKey('product', p.id), payload, token);
      const rid = r?.data?.product_id;
      if (rid) results.push({ oldId: p.id, rid, collection: 'products' });
    } catch (e) {
      console.warn('[Migration] product failed:', p.productName, e.message);
    }
  }
  return { results, attempted };
}

async function migrateCustomers(records, businessId, token) {
  const results = []; let attempted = 0;
  for (const c of records) {
    if (hasRid(c) || c.deleted) continue;
    attempted++;
    try {
      const payload = {
        business_id: businessId,
        customer: {
          customer_name: c.name || c.customerName || '',
          customer_phone: c.phone || '',
          customer_email: c.email || '',
          address: c.address || '',
        },
      };
      const r = await rpc('upsert_customer', migrateKey('customer', c.id), payload, token);
      const rid = r?.data?.customer_id;
      if (rid) results.push({ oldId: c.id, rid, collection: 'customers' });
    } catch (e) {
      console.warn('[Migration] customer failed:', c.name, e.message);
    }
  }
  return { results, attempted };
}

async function migrateSuppliers(records, businessId, token) {
  const results = []; let attempted = 0;
  for (const s of records) {
    if (hasRid(s) || s.deleted) continue;
    attempted++;
    try {
      const payload = {
        business_id: businessId,
        supplier: {
          supplier_name: s.name || s.supplierName || '',
          phone: s.phone || s.contact || '',
          email: s.email || '',
          address: s.address || '',
        },
      };
      const r = await rpc('upsert_supplier', migrateKey('supplier', s.id), payload, token);
      const rid = r?.data?.supplier_id;
      if (rid) results.push({ oldId: s.id, rid, collection: 'suppliers' });
    } catch (e) {
      console.warn('[Migration] supplier failed:', s.name, e.message);
    }
  }
  return { results, attempted };
}

async function migrateExpenseCategories(records, businessId, token) {
  const results = []; let attempted = 0;
  for (const c of records) {
    if (hasRid(c) || c.deleted) continue;
    attempted++;
    try {
      const payload = {
        business_id: businessId,
        category: { name: c.name || '' },
      };
      const r = await rpc('upsert_expense_category', migrateKey('expcat', c.id), payload, token);
      const rid = r?.data?.expense_category_id;
      if (rid) results.push({ oldId: c.id, rid, collection: 'expenseCategories' });
    } catch (e) {
      console.warn('[Migration] expense category failed:', c.name, e.message);
    }
  }
  return { results, attempted };
}

// ── Transaction migration ─────────────────────────────────────────────────

async function migrateSales(records, businessId, token) {
  const results = []; let attempted = 0;
  for (const s of records) {
    if (hasRid(s) || s.deleted) continue;
    attempted++;
    try {
      const saleDate = s.date || new Date().toISOString().split('T')[0];
      const items = (s.items || [{ productName: s.productName, quantity: s.quantity, sellingPrice: s.sellingPrice, cost: s.cost, type: s.isService ? 'service' : 'product' }]).map(it => ({
        item_type: 'MANUAL',
        product_id: null,
        service_id: null,
        product_name: it.productName || '',
        description: it.description || it.productName || 'Item',
        quantity: parseFloat(it.quantity || 0),
        unit_cost: parseFloat(it.cost || 0),
        selling_price: parseFloat(it.sellingPrice || 0),
      }));
      const payments = (s.payments || [])
        .filter(p => p.amount && parseFloat(p.amount) > 0)
        .map(p => ({
          payment_account_id: null,
          wallet_type: 'credit',
          amount: parseFloat(p.amount),
          payment_method: p.type || p.method || 'cash',
          payment_date: saleDate,
        }));

      // If no explicit payments array, create one from the total
      if (payments.length === 0 && s.isPaid !== false && parseFloat(s.totalRevenue || 0) > 0) {
        payments.push({
          payment_account_id: null,
          wallet_type: 'credit',
          amount: parseFloat(s.totalRevenue || 0),
          payment_method: s.paymentMethod || 'cash',
          payment_date: saleDate,
        });
      }

      const sale = {
        customer_name: s.customerName || null,
        customer_id: null,
        sale_date: saleDate,
        total_revenue: parseFloat(s.totalRevenue || 0),
        total_cost: parseFloat(s.totalCost || 0),
        profit: parseFloat(s.profit || 0),
        is_paid: !!s.isPaid,
      };

      const r = await rpc('create_sale', migrateKey('sale', s.id), {
        business_id: businessId,
        sale,
        items,
        movements: [], // Skip inventory movements for historical data
        payments,
      }, token);
      const rid = r?.data?.sale_id;
      if (rid) results.push({ oldId: s.id, rid, collection: 'sales' });
    } catch (e) {
      console.warn('[Migration] sale failed:', s.id, e.message);
    }
  }
  return { results, attempted };
}

async function migratePurchases(records, businessId, token) {
  const results = []; let attempted = 0;
  for (const p of records) {
    if (hasRid(p) || p.deleted) continue;
    attempted++;
    try {
      const purchaseDate = p.date || new Date().toISOString().split('T')[0];
      const items = (p.items || [{ productName: p.productName, quantity: p.quantity, costPrice: p.costPrice }]).map(it => {
        const qty = parseFloat(it.quantity || 0);
        const unitCost = parseFloat(it.unitPrice || it.costPrice || it.cost || 0);
        const lineCost = parseFloat(it.totalCost || 0) || (qty * unitCost);
        return {
          item_type: 'MANUAL',
          product_id: null,
          product_name: it.productName || '',
          description: it.description || it.productName || 'Item',
          quantity: qty,
          unit_cost: unitCost,
          line_cost: lineCost,
        };
      });
      const payments = (p.payments || [])
        .filter(pay => pay.amount && parseFloat(pay.amount) > 0)
        .map(pay => ({
          payment_account_id: null,
          wallet_type: 'credit',
          amount: parseFloat(pay.amount),
          payment_method: pay.type || 'cash',
          payment_date: purchaseDate,
        }));

      if (payments.length === 0 && p.isPaid !== false && parseFloat(p.totalCost || 0) > 0) {
        payments.push({
          payment_account_id: null,
          wallet_type: 'credit',
          amount: parseFloat(p.totalCost || 0),
          payment_method: p.paymentMethod || 'cash',
          payment_date: purchaseDate,
        });
      }

      const purchase = {
        supplier_id: null,
        supplier_name: p.supplier || null,
        purchase_date: purchaseDate,
        total_cost: items.reduce((sum, it) => sum + (it.line_cost || 0), 0),
        is_paid: !!p.isPaid,
      };

      // Step 1: create draft
      const draftResult = await rpc('create_purchase_draft', migrateKey('pdraft', p.id), {
        business_id: businessId,
        purchase,
        items,
      }, token);

      const purchaseId = draftResult?.data?.purchase_id;
      if (!purchaseId) continue;

      // Step 2: post (no movements for historical data)
      const r = await rpc('post_purchase', migrateKey('ppost', p.id), {
        business_id: businessId,
        purchase_id: purchaseId,
        row_version: 1,
        movements: [],
        payments,
      }, token);
      if (purchaseId) results.push({ oldId: p.id, rid: purchaseId, collection: 'purchases' });
    } catch (e) {
      console.warn('[Migration] purchase failed:', p.id, e.message);
    }
  }
  return { results, attempted };
}

async function migrateExpenses(records, businessId, token) {
  const results = []; let attempted = 0;
  for (const e of records) {
    if (hasRid(e) || e.deleted) continue;
    attempted++;
    try {
      const expenseDate = e.date || new Date().toISOString().split('T')[0];
      const expense = {
        category_id: null,
        category_name: e.category || e.expenseCategory || null,
        expense_date: expenseDate,
        amount: parseFloat(e.amount || 0),
        description: e.description || e.name || '',
        is_paid: !!e.isPaid,
      };

      // Step 1: create draft
      const draftResult = await rpc('create_expense_draft', migrateKey('edraft', e.id), {
        business_id: businessId,
        expense,
      }, token);

      const expenseId = draftResult?.data?.expense_id;
      if (!expenseId) continue;

      // Step 2: post with payments
      const payments = [];
      if (e.isPaid !== false) {
        if (Array.isArray(e.payments) && e.payments.length > 0) {
          e.payments.forEach(p => {
            if (p.amount && parseFloat(p.amount) > 0) {
              payments.push({
                payment_account_id: null,
                wallet_type: 'credit',
                amount: parseFloat(p.amount),
                payment_method: p.type || 'cash',
                payment_date: expenseDate,
              });
            }
          });
        } else {
          payments.push({
            payment_account_id: null,
            wallet_type: 'credit',
            amount: parseFloat(e.amount || 0),
            payment_method: e.paymentMethod || 'cash',
            payment_date: expenseDate,
          });
        }
      }

      await rpc('post_expense', migrateKey('epost', e.id), {
        business_id: businessId,
        expense_id: expenseId,
        row_version: 1,
        payments,
      }, token);
      results.push({ oldId: e.id, rid: expenseId, collection: 'expenses' });
    } catch (err) {
      console.warn('[Migration] expense failed:', e.id, err.message);
    }
  }
  return { results, attempted };
}

// ── Main migration entry point ────────────────────────────────────────────

/**
 * Run one-time legacy data migration for the current user/business.
 *
 * @param {string} uid - User ID (for localStorage key prefix)
 * @param {string} businessId - Current business UUID
 * @param {string} token - Supabase access token
 * @param {object} collections - { products, customers, suppliers, expenseCategories, sales, purchases, expenses }
 * @param {function} onUpdate - callback(collection, oldId, rid) to update state with new _rid
 * @returns {object} { migrated: number, errors: number }
 */
export async function runLegacyMigration(uid, businessId, token, collections, onUpdate) {
  const flagKey = `faida_migration_done_${uid}_${businessId}`;
  if (localStorage.getItem(flagKey) === 'true') {
    console.log('[Migration] Already completed for this user/business.');
    return { migrated: 0, errors: 0, skipped: true };
  }

  console.log('[Migration] Starting legacy data migration...');
  let totalMigrated = 0;
  let totalErrors = 0;

  let totalAttempted = 0;

  const applyResults = (results, attempted) => {
    results.forEach(({ oldId, rid, collection }) => {
      if (onUpdate) onUpdate(collection, oldId, rid);
    });
    totalMigrated += results.length;
    totalAttempted += attempted;
    totalErrors += (attempted - results.length);
  };

  try {
    // Phase 1: Master data (must come first so transactions can reference them)
    const prod = await migrateProducts(collections.products || [], businessId, token);
    applyResults(prod.results, prod.attempted);

    const cust = await migrateCustomers(collections.customers || [], businessId, token);
    applyResults(cust.results, cust.attempted);

    const supp = await migrateSuppliers(collections.suppliers || [], businessId, token);
    applyResults(supp.results, supp.attempted);

    const cat = await migrateExpenseCategories(collections.expenseCategories || [], businessId, token);
    applyResults(cat.results, cat.attempted);

    // Phase 2: Transactions
    const sale = await migrateSales(collections.sales || [], businessId, token);
    applyResults(sale.results, sale.attempted);

    const purch = await migratePurchases(collections.purchases || [], businessId, token);
    applyResults(purch.results, purch.attempted);

    const exp = await migrateExpenses(collections.expenses || [], businessId, token);
    applyResults(exp.results, exp.attempted);

  } catch (e) {
    console.error('[Migration] Unexpected error:', e);
    totalErrors++;
  }

  // Mark migration as done only if nothing failed
  if (totalErrors === 0) {
    localStorage.setItem(flagKey, 'true');
  }

  console.log(`[Migration] Complete: ${totalMigrated} records migrated, ${totalErrors} errors.`);
  return { migrated: totalMigrated, errors: totalErrors, skipped: false };
}
